import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Bell, BellOff, Send, TestTube, RefreshCw, CheckCircle, XCircle, Zap } from 'lucide-react';

const GestionNotificacionesPush = () => {
  const API = `${window.location.origin}/api`;
  
  const [config, setConfig] = useState({
    enabled_global: true,
    enabled_grades: true,
    enabled_assignments: true,
    enabled_scholarships: true,
    enabled_messages: true,
    enabled_reminders: true,
    enabled_formularios: true,
    enabled_pedidos_oracion: true,
    onesignal_app_id: '',
    onesignal_rest_api_key: '',
    courier_api_key: '',
    courier_client_key: '',
    active_provider: 'onesignal'
  });
  
  const [providers, setProviders] = useState({
    onesignal: { configured: false },
    courier: { configured: false }
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [changingProvider, setChangingProvider] = useState(false);
  const [testingProvider, setTestingProvider] = useState(null);
  const [testNotification, setTestNotification] = useState({
    title: '',
    message: '',
    role: 'all'
  });
  
  useEffect(() => {
    loadConfig();
    loadProviders();
  }, []);
  
  const loadConfig = async () => {
    try {
      const response = await axios.get(`${API}/notifications/config`);
      setConfig(response.data);
    } catch (error) {
      console.error('Error loading config:', error);
      toast.error('Error al cargar configuración');
    } finally {
      setLoading(false);
    }
  };
  
  const loadProviders = async () => {
    try {
      const response = await axios.get(`${API}/notifications/providers`);
      if (response.data.success) {
        setProviders(response.data.providers);
        setConfig(prev => ({
          ...prev,
          active_provider: response.data.active_provider
        }));
      }
    } catch (error) {
      console.error('Error loading providers:', error);
    }
  };
  
  const saveConfig = async () => {
    try {
      setSaving(true);
      await axios.put(`${API}/notifications/config`, config);
      toast.success('Configuración guardada exitosamente');
      loadProviders(); // Recargar estado de proveedores
    } catch (error) {
      console.error('Error saving config:', error);
      toast.error('Error al guardar configuración');
    } finally {
      setSaving(false);
    }
  };
  
  const handleToggle = (field) => {
    setConfig(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };
  
  const changeProvider = async (newProvider) => {
    if (newProvider === config.active_provider) {
      toast.info(`${newProvider === 'onesignal' ? 'OneSignal' : 'Courier'} ya está activo`);
      return;
    }
    
    // Verificar si el proveedor está configurado
    const providerInfo = providers[newProvider];
    if (!providerInfo?.configured) {
      toast.error(`${newProvider === 'onesignal' ? 'OneSignal' : 'Courier'} no está configurado. Guarda las credenciales primero.`);
      return;
    }
    
    try {
      setChangingProvider(true);
      const response = await axios.put(`${API}/notifications/provider?provider=${newProvider}`);
      
      if (response.data.success) {
        setConfig(prev => ({ ...prev, active_provider: newProvider }));
        toast.success(response.data.message);
        loadProviders();
      }
    } catch (error) {
      console.error('Error changing provider:', error);
      toast.error(error.response?.data?.detail || 'Error al cambiar proveedor');
    } finally {
      setChangingProvider(false);
    }
  };
  
  const testProvider = async (provider) => {
    try {
      setTestingProvider(provider);
      const response = await axios.post(`${API}/notifications/test-provider/${provider}`);
      
      if (response.data.success) {
        toast.success(`✅ ${response.data.provider}: Prueba exitosa (${response.data.recipients || 'enviado'})`);
      } else {
        toast.error(`❌ ${response.data.provider}: ${response.data.error}`);
      }
    } catch (error) {
      console.error('Error testing provider:', error);
      toast.error(`Error al probar ${provider}`);
    } finally {
      setTestingProvider(null);
    }
  };
  
  const sendTestNotification = async () => {
    try {
      await axios.post(`${API}/notifications/test`);
      toast.success('Notificación de prueba enviada. Revisa tu navegador.');
    } catch (error) {
      console.error('Error sending test:', error);
      toast.error('Error al enviar notificación de prueba');
    }
  };
  
  const [syncing, setSyncing] = useState(false);
  
  const syncSubscriptions = async () => {
    try {
      setSyncing(true);
      toast.info('Sincronizando subscription IDs desde OneSignal...');
      const response = await axios.post(`${API}/notifications/sync-subscriptions`);
      if (response.data.success) {
        toast.success(`✅ Sincronización completada: ${response.data.actualizados} usuarios actualizados`);
      } else {
        toast.error(response.data.error || 'Error en sincronización');
      }
    } catch (error) {
      console.error('Error syncing:', error);
      toast.error('Error al sincronizar subscriptions');
    } finally {
      setSyncing(false);
    }
  };
  
  const sendCustomNotification = async () => {
    if (!testNotification.title || !testNotification.message) {
      toast.error('Complete título y mensaje');
      return;
    }
    
    try {
      const payload = {
        title: testNotification.title,
        message: testNotification.message,
        role: testNotification.role === 'all' ? null : testNotification.role
      };
      
      await axios.post(`${API}/notifications/send-push`, payload);
      toast.success('Notificación enviada exitosamente');
      setTestNotification({ title: '', message: '', role: 'all' });
    } catch (error) {
      console.error('Error sending notification:', error);
      toast.error(error.response?.data?.detail || 'Error al enviar notificación');
    }
  };
  
  if (loading) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="text-center">Cargando configuración...</div>
      </div>
    );
  }
  
  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
          <Bell className="h-8 w-8 text-blue-600" />
          Gestión de Notificaciones Push
        </h1>
        <p className="text-gray-600 mt-2">
          Configura qué tipos de notificaciones se envían y elige el proveedor
        </p>
      </div>

      {/* Selector de Proveedor Multi-Provider */}
      <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-lg p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4 text-gray-900 flex items-center gap-2">
          <Zap className="h-5 w-5 text-purple-600" />
          Proveedor de Notificaciones Activo
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          Selecciona el servicio que enviará las notificaciones push. Puedes cambiar entre proveedores sin perder la configuración.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* OneSignal Card */}
          <div 
            className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
              config.active_provider === 'onesignal' 
                ? 'border-green-500 bg-green-50' 
                : 'border-gray-200 bg-white hover:border-gray-300'
            } ${!providers.onesignal?.configured ? 'opacity-60' : ''}`}
            onClick={() => changeProvider('onesignal')}
          >
            <div className="flex items-center justify-between mb-2">
              <span className="font-semibold text-lg">🔔 OneSignal</span>
              <div className="flex items-center gap-2">
                {providers.onesignal?.configured ? (
                  <CheckCircle className="h-5 w-5 text-green-500" />
                ) : (
                  <XCircle className="h-5 w-5 text-red-400" />
                )}
                {config.active_provider === 'onesignal' && (
                  <span className="px-2 py-1 bg-green-500 text-white text-xs rounded-full">ACTIVO</span>
                )}
              </div>
            </div>
            <p className="text-sm text-gray-600">Servicio principal de notificaciones push</p>
            <div className="mt-3 flex gap-2">
              <button
                onClick={(e) => { e.stopPropagation(); testProvider('onesignal'); }}
                disabled={!providers.onesignal?.configured || testingProvider === 'onesignal'}
                className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 disabled:opacity-50"
              >
                {testingProvider === 'onesignal' ? 'Probando...' : 'Probar'}
              </button>
            </div>
          </div>
          
          {/* Courier Card — OCULTO (Courier removido 2026-05-05). Cambiar false → true para volver a mostrar */}
          {false && (
          <div 
            className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
              config.active_provider === 'courier' 
                ? 'border-green-500 bg-green-50' 
                : 'border-gray-200 bg-white hover:border-gray-300'
            } ${!providers.courier?.configured ? 'opacity-60' : ''}`}
            onClick={() => changeProvider('courier')}
          >
            <div className="flex items-center justify-between mb-2">
              <span className="font-semibold text-lg">📦 Courier</span>
              <div className="flex items-center gap-2">
                {providers.courier?.configured ? (
                  <CheckCircle className="h-5 w-5 text-green-500" />
                ) : (
                  <XCircle className="h-5 w-5 text-red-400" />
                )}
                {config.active_provider === 'courier' && (
                  <span className="px-2 py-1 bg-green-500 text-white text-xs rounded-full">ACTIVO</span>
                )}
              </div>
            </div>
            <p className="text-sm text-gray-600">Servicio alternativo multi-canal</p>
            <div className="mt-3 flex gap-2">
              <button
                onClick={(e) => { e.stopPropagation(); testProvider('courier'); }}
                disabled={!providers.courier?.configured || testingProvider === 'courier'}
                className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 disabled:opacity-50"
              >
                {testingProvider === 'courier' ? 'Probando...' : 'Probar'}
              </button>
            </div>
          </div>
          )}
        </div>
        
        {changingProvider && (
          <div className="mt-4 text-center text-purple-600">
            <RefreshCw className="h-5 w-5 animate-spin inline mr-2" />
            Cambiando proveedor...
          </div>
        )}
        
        {/* Botón de sincronización */}
        <div className="mt-4 p-4 bg-gray-50 rounded-lg border">
          <h3 className="font-medium text-gray-900 mb-2">🔄 Sincronizar Subscription IDs</h3>
          <p className="text-sm text-gray-600 mb-3">
            Sincroniza los IDs de suscripción de OneSignal para todos los usuarios. 
            Útil si detectás usuarios que no reciben push correctamente.
          </p>
          <button
            onClick={syncSubscriptions}
            disabled={syncing}
            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium disabled:opacity-50 flex items-center gap-2"
          >
            {syncing ? (
              <>
                <RefreshCw className="h-4 w-4 animate-spin" />
                Sincronizando...
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4" />
                Sincronizar Ahora
              </>
            )}
          </button>
        </div>
      </div>

      {/* Configuración OneSignal */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4 text-gray-900">
          🔑 Credenciales OneSignal
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          Configura las credenciales de OneSignal para activar las notificaciones push.
          Obtén estas credenciales en <a href="https://onesignal.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">OneSignal Dashboard</a>.
        </p>
        
        <input type="text" name="fakeusername" style={{position: 'absolute', left: '-9999px'}} tabIndex="-1" />
        <input type="password" name="fakepassword" style={{position: 'absolute', left: '-9999px'}} tabIndex="-1" />
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              App ID de OneSignal
            </label>
            <input
              type="text"
              name="onesignal_application_id"
              value={config.onesignal_app_id || ''}
              onChange={(e) => setConfig(prev => ({ ...prev, onesignal_app_id: e.target.value }))}
              placeholder="ej: 93b4e886-cf19-4b05-bb81-cb64297c1277"
              autoComplete="new-password"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              REST API Key de OneSignal
            </label>
            <input
              type="text"
              name="onesignal_rest_key"
              value={config.onesignal_rest_api_key || ''}
              onChange={(e) => setConfig(prev => ({ ...prev, onesignal_rest_api_key: e.target.value }))}
              placeholder="os_v2_app_..."
              autoComplete="new-password"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Configuración Courier — OCULTO (Courier removido 2026-05-05). Cambiar false → true para volver a mostrar */}
      {false && (
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4 text-gray-900">
          📦 Credenciales Courier (Alternativo)
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          Courier es un proveedor alternativo que puede funcionar como respaldo.
          Obtén tus credenciales en <a href="https://app.courier.com" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:underline">Courier Dashboard</a>.
        </p>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              API Key de Courier
            </label>
            <input
              type="text"
              value={config.courier_api_key || ''}
              onChange={(e) => setConfig(prev => ({ ...prev, courier_api_key: e.target.value }))}
              placeholder="pk_prod_..."
              autoComplete="new-password"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Client Key de Courier (opcional)
            </label>
            <input
              type="text"
              value={config.courier_client_key || ''}
              onChange={(e) => setConfig(prev => ({ ...prev, courier_client_key: e.target.value }))}
              placeholder="MDY5MDc2..."
              autoComplete="new-password"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
          
          <p className="text-xs text-gray-500">
            💡 Courier puede integrarse con OneSignal internamente para mayor redundancia.
          </p>
        </div>
      </div>
      )}
      
      {/* Master Switch */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {config.enabled_global ? (
              <Bell className="h-6 w-6 text-green-600" />
            ) : (
              <BellOff className="h-6 w-6 text-red-600" />
            )}
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Sistema de Notificaciones
              </h3>
              <p className="text-sm text-gray-600">
                Control maestro - Desactiva TODAS las notificaciones
              </p>
            </div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={config.enabled_global}
              onChange={() => handleToggle('enabled_global')}
              className="sr-only peer"
            />
            <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-green-600"></div>
          </label>
        </div>
      </div>
      
      {/* Individual Controls */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">Control por Tipo de Notificación</h2>
        
        <div className="space-y-4">
          {/* Calificaciones */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <div className="font-medium text-gray-900">📊 Nuevas Calificaciones</div>
              <div className="text-sm text-gray-600">Cuando se publican calificaciones</div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={config.enabled_grades}
                onChange={() => handleToggle('enabled_grades')}
                disabled={!config.enabled_global}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 peer-disabled:opacity-50"></div>
            </label>
          </div>
          
          {/* Tareas */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <div className="font-medium text-gray-900">📝 Nuevas Tareas</div>
              <div className="text-sm text-gray-600">Cuando se asignan nuevas tareas</div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={config.enabled_assignments}
                onChange={() => handleToggle('enabled_assignments')}
                disabled={!config.enabled_global}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 peer-disabled:opacity-50"></div>
            </label>
          </div>
          
          {/* Becas */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <div className="font-medium text-gray-900">💰 Becas (Aprobación/Rechazo)</div>
              <div className="text-sm text-gray-600">Actualizaciones de solicitudes de becas</div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={config.enabled_scholarships}
                onChange={() => handleToggle('enabled_scholarships')}
                disabled={!config.enabled_global}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 peer-disabled:opacity-50"></div>
            </label>
          </div>
          
          {/* Mensajes */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <div className="font-medium text-gray-900">📧 Mensajes sin Leer</div>
              <div className="text-sm text-gray-600">Notificaciones de correspondencia interna</div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={config.enabled_messages}
                onChange={() => handleToggle('enabled_messages')}
                disabled={!config.enabled_global}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 peer-disabled:opacity-50"></div>
            </label>
          </div>
          
          {/* Recordatorios */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <div className="font-medium text-gray-900">📅 Recordatorios de Clases</div>
              <div className="text-sm text-gray-600">Avisos de horarios de clases</div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={config.enabled_reminders}
                onChange={() => handleToggle('enabled_reminders')}
                disabled={!config.enabled_global}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 peer-disabled:opacity-50"></div>
            </label>
          </div>
          
          {/* Formularios */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <div className="font-medium text-gray-900">📋 Nuevos Formularios de Evaluación</div>
              <div className="text-sm text-gray-600">Cuando un profesor crea un formulario</div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={config.enabled_formularios}
                onChange={() => handleToggle('enabled_formularios')}
                disabled={!config.enabled_global}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 peer-disabled:opacity-50"></div>
            </label>
          </div>
          
          {/* Pedidos de Oración */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <div className="font-medium text-gray-900">🙏 Pedidos de Oración</div>
              <div className="text-sm text-gray-600">Cuando se crea un nuevo pedido público</div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={config.enabled_pedidos_oracion}
                onChange={() => handleToggle('enabled_pedidos_oracion')}
                disabled={!config.enabled_global}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 peer-disabled:opacity-50"></div>
            </label>
          </div>
        </div>
        
        {/* Save Button */}
        <div className="mt-6 flex justify-end">
          <button
            onClick={saveConfig}
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Guardando...' : 'Guardar Configuración'}
          </button>
        </div>
      </div>
      
      {/* Test Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <TestTube className="h-5 w-5" />
          Probar Notificaciones
        </h2>
        
        <div className="space-y-4">
          <button
            onClick={sendTestNotification}
            className="w-full bg-green-600 hover:bg-green-700 text-white px-4 py-3 rounded-lg font-semibold flex items-center justify-center gap-2"
          >
            <Send className="h-5 w-5" />
            Enviarme una Notificación de Prueba
          </button>
          
          <div className="border-t pt-4">
            <h3 className="font-medium mb-3">Enviar Notificación Personalizada</h3>
            <div className="space-y-3">
              <input
                type="text"
                placeholder="Título de la notificación"
                value={testNotification.title}
                onChange={(e) => setTestNotification({...testNotification, title: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              <textarea
                placeholder="Mensaje de la notificación"
                value={testNotification.message}
                onChange={(e) => setTestNotification({...testNotification, message: e.target.value})}
                rows="3"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              <select
                value={testNotification.role}
                onChange={(e) => setTestNotification({...testNotification, role: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">Todos los usuarios</option>
                <option value="admin">Solo Administradores</option>
                <option value="profesor">Solo Profesores</option>
                <option value="estudiante">Solo Estudiantes</option>
                <option value="pastor">Solo Pastores</option>
              </select>
              <button
                onClick={sendCustomNotification}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-3 rounded-lg font-semibold flex items-center justify-center gap-2"
              >
                <Send className="h-5 w-5" />
                Enviar Notificación
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex gap-3">
          <Bell className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-900">
            <p className="font-medium mb-1">Información Importante:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Los usuarios deben permitir notificaciones en su navegador</li>
              <li>Las notificaciones funcionan en PC y celular</li>
              <li>El sistema maestro desactiva TODAS las notificaciones</li>
              <li><strong>Multi-proveedor:</strong> Puedes cambiar entre OneSignal y Courier sin perder configuración</li>
              <li>Los cambios se aplican inmediatamente</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GestionNotificacionesPush;
